
















import { defineComponent, ref } from "@vue/composition-api";
import {
  InputPersonalInformation,
  PersonalInformation,
} from "@scrile/api-provider/dist/api/PersonalInformationProvider";
import {
  InputUserPublicInformationUpdate,
  UserPublicInformation,
} from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { formatDateOnly, toDateOnlyISO } from "@/lib/time";
import config from "@/config";
import BaseForm from "@/components/BaseForm";
import { FormValues } from "@/types.d";
import PhoneVerifyModal from "./PhoneVerifyModal.vue";
import { personalInfoFields } from "../../../config";

export default defineComponent({
  name: "PersonalInfoForm",
  components: { BaseForm, PhoneVerifyModal },
  props: {
    hideDescription: { type: Boolean, default: false },
  },
  setup(_, { emit }) {
    const { authUserStrict } = useAuthUser();
    const personalInformation = ref<PersonalInformation | null>(null);
    const userPublicInformation = ref<UserPublicInformation | null>(null);
    const showPhoneVerify = ref(false);
    const phoneToVerify = ref("");
    let phoneVerifyPending: { resolve: () => void; reject: (e: Error) => void } | null = null;
    const values = ref<FormValues>({
      firstName: "",
      middleName: "",
      lastName: "",
      dateOfBirth: "",
      phone: "",
      smsNotifications: true,
    });

    const getUserPublicInformation = async () => {
      userPublicInformation.value = await providers.UserPublicDataProvider.userPublicInformationFind(
        authUserStrict.value.id
      );
      if (!userPublicInformation.value) return;
      values.value = {
        ...values.value,
        dateOfBirth: userPublicInformation.value.dateOfBirth
          ? formatDateOnly(userPublicInformation.value.dateOfBirth)
          : undefined,
      };
    };

    const getPersonalInfo = async () => {
      personalInformation.value = await providers.PersonalInformationProvider.find({ id: authUserStrict.value.id });
      if (!personalInformation.value) return;
      values.value = {
        ...values.value,
        firstName: personalInformation.value.firstName,
        middleName: personalInformation.value.middleName,
        lastName: personalInformation.value.lastName,
        phone: personalInformation.value.phone,
      };
    };

    const getData = async () => {
      await Promise.all([getPersonalInfo(), getUserPublicInformation()]);
    };

    const submitPersonalInfo = async () => {
      const data: InputPersonalInformation = {
        userId: authUserStrict.value.id,
        firstName: values.value.firstName,
        middleName: values.value.middleName,
        lastName: values.value.lastName,
        phone: values.value.phone,
      };
      personalInformation.value = await providers.PersonalInformationProvider.update({ data });
      values.value = {
        ...values.value,
        firstName: personalInformation.value.firstName,
        middleName: personalInformation.value.middleName,
        lastName: personalInformation.value.lastName,
        phone: personalInformation.value.phone,
      };
    };

    const submitPublicInfo = async () => {
      const data: InputUserPublicInformationUpdate = {
        userId: authUserStrict.value.id,
        dateOfBirth: toDateOnlyISO(values.value.dateOfBirth),
      };
      userPublicInformation.value = await providers.UserPublicDataProvider.userPublicInformationUpdate(data);
      values.value = {
        ...values.value,
        dateOfBirth: userPublicInformation.value.dateOfBirth
          ? formatDateOnly(userPublicInformation.value.dateOfBirth)
          : undefined,
      };
    };

    const onPhoneVerified = () => {
      showPhoneVerify.value = false;
      phoneVerifyPending?.resolve();
      phoneVerifyPending = null;
    };

    const onPhoneVerifyClose = () => {
      showPhoneVerify.value = false;
      phoneVerifyPending?.reject(new Error("Phone verification cancelled"));
      phoneVerifyPending = null;
    };

    const ensurePhoneVerified = async () => {
      const phone = String(values.value.phone ?? "");
      const verification = await providers.PhoneVerificationProvider.find();
      if (verification.phone === phone) return;

      await providers.PhoneVerificationProvider.start({ phone });
      phoneToVerify.value = phone;
      showPhoneVerify.value = true;
      await new Promise<void>((resolve, reject) => {
        phoneVerifyPending = { resolve, reject };
      });
    };

    const submitSmsConsent = async () => {
      await providers.SmsNotificationsProvider.consentUpdate({ enabled: Boolean(values.value.smsNotifications) });
    };

    const submitForm = async () => {
      if (config.featureSmsNotifications) {
        await ensurePhoneVerified();
        await submitSmsConsent();
      }
      await Promise.all([submitPersonalInfo(), submitPublicInfo()]);
    };

    const created = async () => {
      emit("setValid", true);
      emit("setCb", () => submitForm());
      try {
        emit("setLoading", true);
        await getData();
      } finally {
        emit("setLoading", false);
      }
    };

    created();

    return {
      values,
      personalInfoFields: personalInfoFields(),
      showPhoneVerify,
      phoneToVerify,
      onPhoneVerified,
      onPhoneVerifyClose,
    };
  },
});
